import {
  Button,
  Center,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  CloseCircleOutline,
  LockOutline,
  UnlockOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import useFetchCard from 'app/hooks/useFetchCard';
import useLockCard from 'app/hooks/useLockCard';
import useUnlockCard from 'app/hooks/useUnlockCard';

import { ManageCardLockModalProps } from './types';

function ManageCardLockModal({
  id,
  employeeName = '',
  isOpen,
  onClose,
  onManageCardLock,
}: ManageCardLockModalProps) {
  const toast = useToast();

  const { data: card, isFetching: isLoadingCard } = useFetchCard({ id }, isOpen);

  const { mutate: unlockCard, isLoading: isUnlockingCard } = useUnlockCard({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Aslan card unlocked',
        description: `${employeeName} will be notified that their card has been unlocked.`,
      });

      onManageCardLock();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to unlock the card. Please try again.',
      });
    },
  });

  const { mutate: lockCard, isLoading: isLockingCard } = useLockCard({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Aslan card locked',
        description: `${employeeName} will be notified that their card has been locked by an administrator.`,
      });

      onManageCardLock();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to lock the card. Please try again.',
      });
    },
  });

  const isCardLocked =
    card?.cardStatus === 'LOCKED' && card.statusChangedReason !== 'USER_APP_FREEZE';

  const title = isCardLocked ? 'Unlock card' : 'Lock card';

  const LockIcon = isCardLocked ? UnlockOutline : LockOutline;

  const lockAction = isCardLocked ? unlockCard : lockCard;

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">{title}</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Manage card lock"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        {isLoadingCard ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <>
            <ModalBody mt="20px">
              {isCardLocked ? (
                <Stack gap="16px">
                  <Text color="brand.mutedPurple" textStyle="body">
                    Are you sure you want to unlock this user&apos;s card?{' '}
                  </Text>
                  <Text color="brand.mutedPurple" textStyle="body">
                    This action will reactivate the card for the employee{' '}
                    <Text as="span" textStyle="h6SemiBold">
                      {employeeName}
                    </Text>
                    . They will be notified that their card is now active again.
                  </Text>
                </Stack>
              ) : (
                <Stack gap="16px">
                  <Text color="brand.mutedPurple" textStyle="body">
                    You are about to lock this user&apos;s card.
                  </Text>
                  <Text color="brand.mutedPurple" textStyle="body">
                    This means they will no longer be able to use their card, and any purchase
                    attempts will be blocked.
                  </Text>
                </Stack>
              )}
            </ModalBody>
            <ModalFooter>
              <>
                <Button variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant={isCardLocked ? 'solid' : 'danger'}
                  size="sm"
                  rightIcon={<LockIcon boxSize="20px" />}
                  isLoading={isLockingCard || isUnlockingCard}
                  onClick={() => lockAction({ id })}
                >
                  {isCardLocked ? 'Unlock' : 'Lock'}
                </Button>
              </>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default ManageCardLockModal;
